import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Col, Container, Row, Form, Input, Label, FormFeedback, Alert, Spinner } from 'reactstrap';
import ParticlesAuth from '../authenticationInner/ParticlesAuth';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { resetPassword, resetPasswordState } from '../../slices/veloxity/auth/ResetPasswordReducer';
import AImeeLogo from "../../assets/images/AIMEE-LOGO.png";
import AIBBackground from "../../assets/images/ai-background.jpg";
import "../../assets/scss/CreatePassword.css";
import { showErrorAlert, showSuccessAlert } from '../../components/custom/sweetalertHelper';

const NewPasswordPage = (props) => {
  document.title = "Create New Password | Veloxity Dashboard";

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [passwordShow, setPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);

  const { loading, error, successMsg, errorMsg } = useSelector((state) => state.resetPassword || {});

  useEffect(() => {
    return () => {
      dispatch(resetPasswordState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (errorMsg) {
      showErrorAlert('Error', errorMsg);
    }
    if (successMsg) {
      showSuccessAlert('Password Updated', 'Your password has been successfully updated. Redirecting to login page...', 2000);
      setTimeout(() => {
        navigate('/login');
      }, 2000); 
    }
  }, [errorMsg, successMsg, navigate]);


  const query = new URLSearchParams(location.search);
  const token = query.get('token');

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .max(25, 'Password must be less than 25 characters')
        .matches(/.*[a-z].*/, 'Password must contain at least one lowercase letter')
        .matches(/.*[A-Z].*/, 'Password must contain at least one uppercase letter')
        .matches(/.*\d.*/, 'Password must contain at least one digit')
        .matches(/.*[!@#$%^&*(),.?":{}|<>].*/, 'Password must contain at least one special character')
        .required("This field is required"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref('password'), null], "Both passwords need to be the same")
        .required("Confirm Password is required"),
    }),
    onSubmit: (values) => {
      if (token) {
        dispatch(resetPassword({ token, password: values.password }));
      }
    }
  });

  return (
   
      <div className="bg-pattern">
      <div className="bg-overlay"></div>
      <ParticlesAuth>
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
            <Card className="mt-4 card-bg-fill" style={{ border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.9)' }}> {/* Genel border ve arka plan rengi */}
            <CardBody className="p-4">
                <div className="text-center mt-2">
                <img src={AImeeLogo} alt="AIMEE Logo" style={{ height: "20vh", marginBottom: "30px" }} />
              </div>
                  <div className="text-center mt-2">
                    <h5 className="text-primary">Create New Password</h5>
                    <p className="text-muted">Please create a new password for your account.</p>
                  </div>

                  <div className="p-2">
                    {error && <Alert color="danger">{errorMsg}</Alert>}
                    {successMsg && <Alert color="success">{successMsg}</Alert>}
                    <Form onSubmit={validation.handleSubmit}>
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="password-input">Password</Label>
                        <div className="position-relative auth-pass-inputgroup">
                          <Input
                            type={passwordShow ? "text" : "password"}
                            className="form-control pe-5 password-input"
                            placeholder="Enter password"
                            id="password-input"
                            name="password"
                            value={validation.values.password}
                            onBlur={validation.handleBlur}
                            onChange={validation.handleChange}
                            invalid={validation.errors.password && validation.touched.password ? true : false}
                          />
                          {validation.errors.password && validation.touched.password ? (
                            <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                          ) : null}
                          <Button color="link" onClick={() => setPasswordShow(!passwordShow)} className="position-absolute end-0 top-0 text-decoration-none text-muted password-addon" type="button"
                            id="password-addon"><i className="ri-eye-fill align-middle"></i></Button>
                        </div>
                        <div id="passwordInput" className="form-text">Must be at least 8 characters and contain at least one lowercase letter, one uppercase letter, one digit, and one special character.</div>
                      </div>

                      <div className="mb-3">
                        <Label className="form-label" htmlFor="confirm-password-input">Confirm Password</Label>
                        <div className="position-relative auth-pass-inputgroup mb-3">
                          <Input
                            type={confirmPasswordShow ? "text" : "password"}
                            className="form-control pe-5 password-input"
                            placeholder="Confirm password"
                            id="confirm-password-input"
                            name="confirm_password"
                            value={validation.values.confirm_password}
                            onBlur={validation.handleBlur}
                            onChange={validation.handleChange}
                            invalid={validation.errors.confirm_password && validation.touched.confirm_password ? true : false}
                          />
                          {validation.errors.confirm_password && validation.touched.confirm_password ? (
                            <FormFeedback type="invalid">{validation.errors.confirm_password}</FormFeedback>
                          ) : null}
                          <Button color="link" onClick={() => setConfirmPasswordShow(!confirmPasswordShow)} className="position-absolute end-0 top-0 text-decoration-none text-muted password-addon" type="button">
                            <i className="ri-eye-fill align-middle"></i></Button>
                        </div>
                      </div>

                      <div id="password-contain" className="p-3 bg-light mb-2 rounded">
                        <h5 className="fs-13">Password must contain:</h5>
                        <p id="pass-length" className="invalid fs-12 mb-2">Minimum <b>8 characters</b> and a maximum of <b>25 characters</b></p>
                        <p id="pass-lower" className="invalid fs-12 mb-2">At <b>least one lowercase</b> letter (a-z)</p>
                        <p id="pass-upper" className="invalid fs-12 mb-2">At <b>least one uppercase</b> letter (A-Z)</p>
                        <p id="pass-number" className="invalid fs-12 mb-0">At <b>least one number</b> (0-9)</p>
                        <p id="pass-special" className="invalid fs-12 mb-0">At <b>least one special character</b> (!@#$%^&+=)</p>
                      </div>

                      <div className="mt-4">
                        <Button color="success" className="w-100" type="submit" disabled={loading}>
                          {loading ? <Spinner size="sm" /> : 'Update Password'}
                        </Button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-4 text-center">
                <p className="mb-0" style={{color: 'white'}}>Already have an account? <Link to="/login" className="fw-semibold text-primary text-decoration-underline"> Sign in here </Link> </p>
              </div>
            </Col>
          </Row>
        </Container>
        </ParticlesAuth>
      </div>

  );
};

NewPasswordPage.propTypes = {
  history: PropTypes.object,
};

export default NewPasswordPage;
